import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@buildex/ui';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { roleCreated, roleDeleted } from '@/entities/tenant/model';
import type { AdminTenant } from '@/entities/tenant/types';
import { loadModulePermissions, type ModulePermissions } from '@/shared/module-meta';

interface RoleBuilderProps {
  tenant: AdminTenant;
}

/**
 * Конструктор ролей (§9 п.5): роль = имя + набор чекбоксов прав.
 * Список прав собирается динамически из permissions манифестов
 * активных модулей тенанта — новый модуль приносит права автоматически.
 */
export function RoleBuilder({ tenant }: RoleBuilderProps) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<ModulePermissions[]>([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { onCreate, onDelete } = useUnit({ onCreate: roleCreated, onDelete: roleDeleted });

  useEffect(() => {
    let cancelled = false;
    void loadModulePermissions(tenant.modules).then((result) => {
      if (!cancelled) setGroups(result);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant.modules]);

  const togglePermission = (permission: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(permission);
      else next.delete(permission);
      return next;
    });
  };

  const submit = () => {
    if (!name || selected.size === 0) return;
    onCreate({ tenantId: tenant.id, name, permissions: [...selected] });
    setName('');
    setSelected(new Set());
    setModalOpened(false);
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t('roles.hint')}
        </Text>
        <Button size="xs" onClick={() => setModalOpened(true)} disabled={groups.length === 0}>
          {t('roles.create')}
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {tenant.roles.map((role) => (
          <Card key={role.id} padding="md">
            <Group justify="space-between" mb="xs">
              <Text fw={650} size="sm">
                {role.name}
              </Text>
              {role.permissions.includes('*') ? (
                <Badge color="brand">{t('roles.all')}</Badge>
              ) : (
                <Tooltip label={t('roles.delete')}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onDelete({ tenantId: tenant.id, roleId: role.id })}
                    aria-label={t('roles.delete')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M4 7h16M9 7V5h6v2m-8 0 1 13h8l1-13" />
                    </svg>
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            <Group gap={4}>
              {role.permissions.includes('*') ? (
                <Text size="xs" c="dimmed">
                  {t('roles.allHint')}
                </Text>
              ) : (
                role.permissions.map((p) => (
                  <Badge key={p} variant="light" color="gray">
                    {p}
                  </Badge>
                ))
              )}
            </Group>
          </Card>
        ))}
      </SimpleGrid>

      {groups.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('roles.noModules')}
        </Text>
      ) : null}

      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title={t('roles.createTitle')} size="lg">
        <Stack gap="sm">
          <TextInput
            label={t('roles.name')}
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          {groups.map((group) => (
            <div key={group.moduleId}>
              <Divider label={group.label} labelPosition="left" mb="xs" />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                {group.permissions.map((permission) => (
                  <Checkbox
                    key={permission}
                    label={permission}
                    checked={selected.has(permission)}
                    onChange={(e) => togglePermission(permission, e.currentTarget.checked)}
                  />
                ))}
              </SimpleGrid>
            </div>
          ))}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setModalOpened(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={!name || selected.size === 0}>
              {t('common.create')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
